import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Power, AlertCircle, Loader2, Globe, Network } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { Property } from '../../types/types';
import { validatePropertyForActivation, activateProperty } from '../../lib/property-activation';

interface ActivateDraftModalProps {
  isOpen: boolean;
  property: Property | null;
  onClose: () => void;
  /** Called after a successful UPDATE; parent should refresh its list state. */
  onActivated: (id: string, patch: { publish: boolean; share: boolean }) => void;
}

export const ActivateDraftModal: React.FC<ActivateDraftModalProps> = ({ isOpen, property, onClose, onActivated }) => {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const isLight = theme !== 'dark';

  const [publish, setPublish] = useState(false);
  const [share, setShare] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset locals each time the modal opens on a new property.
  useEffect(() => {
    if (isOpen) {
      setPublish(false);
      setShare(false);
      setError(null);
    }
  }, [isOpen, property?.id]);

  if (!isOpen || !property) return null;

  const validation = validatePropertyForActivation(property);

  const handleConfirm = async () => {
    if (!validation.isValid || !supabase) return;
    setSubmitting(true);
    setError(null);
    const result = await activateProperty(supabase, property.id as string, {
      publish,
      share,
      profileId: profile?.id,
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.reason || 'No se pudo activar la propiedad');
      return;
    }
    if (result.shareBlocked) {
      alert(`La propiedad se activó pero no se compartió en Intercambio:\n\n${result.shareBlocked}`);
    }
    onActivated(property.id as string, { publish, share: share && !result.shareBlocked });
    onClose();
  };

  // createPortal so the fixed-position overlay isn't reframed by any
  // `animate-page-entry` ancestor transform on the CRM routes.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={submitting ? undefined : onClose} />
      <div className={clsx(
        'relative w-full max-w-md rounded-2xl border shadow-2xl',
        isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-700',
      )}>
        {/* Header */}
        <div className={clsx('p-5 border-b flex items-center gap-3', isLight ? 'border-slate-100' : 'border-white/5')}>
          <div className={clsx('p-2.5 rounded-xl', isLight ? 'bg-amber-100 text-amber-600' : 'bg-amber-500/15 text-amber-400')}>
            <Power size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={clsx('text-base font-bold', isLight ? 'text-slate-900' : 'text-white')}>
              Activar propiedad
            </h3>
            <p className={clsx('text-xs truncate', isLight ? 'text-slate-500' : 'text-slate-400')}>
              {property.title || 'Sin título'}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          {!validation.isValid ? (
            <>
              <div className={clsx('flex items-start gap-2 mb-3 p-3 rounded-xl', isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/10 text-rose-400')}>
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <p className="text-xs font-semibold">Faltan datos antes de poder activarla.</p>
              </div>
              <p className={clsx('text-xs mb-2', isLight ? 'text-slate-600' : 'text-slate-400')}>
                Completa estos campos en la edición:
              </p>
              <ul className={clsx('text-xs space-y-1 list-disc list-inside', isLight ? 'text-slate-700' : 'text-slate-300')}>
                {validation.missing.map(m => <li key={m}>{m}</li>)}
              </ul>
            </>
          ) : (
            <>
              <p className={clsx('text-sm mb-4', isLight ? 'text-slate-700' : 'text-slate-300')}>
                Al activarla, pasará a la pestaña <strong>Activas</strong>. Opcionalmente, puedes publicarla y/o compartirla al mismo tiempo.
              </p>

              {/* Publish checkbox */}
              <label className={clsx(
                'flex items-start gap-3 p-3 rounded-xl cursor-pointer border transition-colors mb-2',
                publish
                  ? isLight ? 'bg-indigo-50 border-indigo-200' : 'bg-indigo-500/10 border-indigo-500/30'
                  : isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10',
              )}>
                <input
                  type="checkbox"
                  checked={publish}
                  onChange={e => setPublish(e.target.checked)}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Globe size={16} className={publish ? 'text-indigo-500' : 'text-slate-400'} />
                  <div>
                    <p className={clsx('text-sm font-semibold', isLight ? 'text-slate-800' : 'text-white')}>También publicar</p>
                    <p className="text-[11px] text-slate-500">Hacer visible en el portal público.</p>
                  </div>
                </div>
              </label>

              {/* Share checkbox */}
              <label className={clsx(
                'flex items-start gap-3 p-3 rounded-xl cursor-pointer border transition-colors',
                share
                  ? isLight ? 'bg-emerald-50 border-emerald-200' : 'bg-emerald-500/10 border-emerald-500/30'
                  : isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10',
              )}>
                <input
                  type="checkbox"
                  checked={share}
                  onChange={e => setShare(e.target.checked)}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Network size={16} className={share ? 'text-emerald-500' : 'text-slate-400'} />
                  <div>
                    <p className={clsx('text-sm font-semibold', isLight ? 'text-slate-800' : 'text-white')}>También compartir</p>
                    <p className="text-[11px] text-slate-500">Permitir co-brokerage con la red Intercambio.</p>
                  </div>
                </div>
              </label>
            </>
          )}

          {error && (
            <div className={clsx('mt-3 p-3 rounded-xl text-xs font-semibold', isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/10 text-rose-400')}>
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={submitting}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50',
              isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-slate-300 hover:bg-white/10',
            )}
          >
            {validation.isValid ? 'Cancelar' : 'Cerrar'}
          </button>
          {validation.isValid && (
            <button
              onClick={handleConfirm}
              disabled={submitting}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-50 flex items-center gap-2',
                isLight ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-500 hover:bg-amber-400',
              )}
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
              Activar
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
